import React from "react"
import { BarChart3, Clock, DollarSign, Hash, Link, Settings, Download } from "lucide-react"

interface Conversation {
  id: string
  title?: string
  conversation_type: string
  primary_persona: string
  project_id: string
  todoist_task_id?: string
  created_at: Date
  updated_at: Date
  last_message_at?: Date
  message_count: number
  metadata?: any
  is_pinned: boolean
  is_archived: boolean
  tags: string[]
  project_name?: string
  project_color?: string
  personas?: any[]
  tools?: any[]
  messages?: any[]
}

interface RightContextPanelProps {
  collapsed: boolean
  conversation: Conversation | null
}

export const RightContextPanel: React.FC<RightContextPanelProps> = ({ collapsed, conversation }) => {
  if (collapsed || !conversation) {
    return null
  }

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date
    return dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const estimatedTokens = conversation.message_count * 150 // Rough estimate
  const estimatedCost = (estimatedTokens / 1000) * 0.002 // Rough GPT-4 pricing

  return (
    <div className="h-full flex flex-col bg-background p-4 overflow-y-auto">
      {/* Conversation Info */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Conversation Details
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Type</span>
            <span className="text-sm font-medium capitalize">{conversation.conversation_type.replace("-", " ")}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Persona</span>
            <span className="text-sm font-medium">{conversation.primary_persona}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Project</span>
            <span className="text-sm font-medium">{conversation.project_name || "Unnamed"}</span>
          </div>

          {conversation.todoist_task_id && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Link className="h-3 w-3" />
                Task
              </span>
              <span className="text-sm font-medium text-primary">{conversation.todoist_task_id.slice(0, 8)}...</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Created</span>
            <span className="text-sm">{formatDate(conversation.created_at)}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Last Activity</span>
            <span className="text-sm">{formatDate(conversation.last_message_at || conversation.updated_at)}</span>
          </div>
        </div>
      </div>

      {/* Tags */}
      {conversation.tags.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Hash className="h-4 w-4" />
            Tags
          </h3>
          <div className="flex flex-wrap gap-1">
            {conversation.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center px-2 py-1 bg-muted rounded text-xs">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Active Tools */}
      {conversation.tools && conversation.tools.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Active Tools
          </h3>
          <div className="space-y-2">
            {conversation.tools.map((tool: any) => (
              <div key={tool.tool_name} className="flex items-center justify-between p-2 bg-muted rounded text-xs">
                <span className="flex items-center gap-1">🛠️ {tool.tool_name}</span>
                <div className={`w-2 h-2 rounded-full ${tool.is_enabled ? "bg-green-500" : "bg-gray-400"}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversation Stats */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Statistics
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground flex items-center gap-1">💬 Messages</span>
            <span className="text-sm font-medium">{conversation.message_count}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground flex items-center gap-1">🔤 Est. Tokens</span>
            <span className="text-sm">{estimatedTokens.toLocaleString()}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              Est. Cost
            </span>
            <span className="text-sm">${estimatedCost.toFixed(4)}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Duration
            </span>
            <span className="text-sm">
              {Math.ceil(
                ((typeof conversation.updated_at === "string"
                  ? new Date(conversation.updated_at)
                  : conversation.updated_at
                ).getTime() -
                  (typeof conversation.created_at === "string"
                    ? new Date(conversation.created_at)
                    : conversation.created_at
                  ).getTime()) /
                  (1000 * 60),
              )}{" "}
              min
            </span>
          </div>
        </div>
      </div>

      {/* Quick Export */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Download className="h-4 w-4" />
          Quick Export
        </h3>

        <div className="space-y-2">
          <button className="w-full text-left p-2 hover:bg-muted rounded text-sm flex items-center gap-2">
            📄 Export as Markdown
          </button>
          <button className="w-full text-left p-2 hover:bg-muted rounded text-sm flex items-center gap-2">
            📊 Export as JSON
          </button>
          <button className="w-full text-left p-2 hover:bg-muted rounded text-sm flex items-center gap-2">
            📋 Copy All Messages
          </button>
        </div>
      </div>

      {/* Conversation Actions */}
      <div className="mt-auto pt-4 border-t border-border">
        <div className="space-y-2">
          <button className="w-full p-2 text-left hover:bg-muted rounded text-sm text-muted-foreground">
            🔄 Regenerate Last Message
          </button>
          <button className="w-full p-2 text-left hover:bg-muted rounded text-sm text-muted-foreground">
            🗂️ Move to Project
          </button>
          <button className="w-full p-2 text-left hover:bg-muted rounded text-sm text-muted-foreground">
            📎 Link to Task
          </button>
          <button className="w-full p-2 text-left hover:bg-muted rounded text-sm text-red-600">
            🗑️ Delete Conversation
          </button>
        </div>
      </div>
    </div>
  )
}
